import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material';
import { useRoutes } from 'hookrouter';
import React from 'react';
import { useClearCache } from 'react-clear-cache';
import { connect } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ForgotPassword } from './components/ForgotPassword';
import { SalesView } from './components/SalesView';
import { SignIn } from './components/SignIn';
import { SignUp } from './components/SignUp';
import { SingleSignOn } from './components/SingleSignOn';
import { AppRoutes } from './constants/AppRoutes';
import { theme } from './theme';
import { AppState } from './types/AppState';
import './toast-styles.css';
import { Theme } from './types/ViewerState';
import { validateColorInput } from './utils/vendorDataUtils';
import { unknownUser } from './types/User';
import { AuthStatus } from './constants/AuthStatus';
import { Permissions } from './constants/Permissions';
import { config } from './config/config';

const routes = {
  [AppRoutes.ForgotPassword]: (): JSX.Element => <ForgotPassword />,
  [`${AppRoutes.Portal}*`]: (): JSX.Element => <SalesView />,
  [AppRoutes.SignIn]: (): JSX.Element => <SignIn />,
  [AppRoutes.SignUp]: (): JSX.Element => <SignUp />,
  [`${AppRoutes.Sso}*`]: (): JSX.Element => <SingleSignOn />,
};

interface StateProps {
  selectedTextColor: string | undefined;
  logoBarColor: string | undefined;
  authStatus: AuthStatus;
  sub: string | undefined;
  userHash: string | undefined;
  email: string;
  name: string;
  group: { groupName: string } | undefined;
  permissions: string[] | undefined;
}

type Props = StateProps;

const AppComponent: React.FC<Props> = ({
  selectedTextColor,
  logoBarColor,
  authStatus,
  sub,
  userHash,
  email,
  name,
  group,
  permissions,
}: StateProps) => {
  const [latestVersionToastOpen, setLatestVersionToastOpen] = React.useState(false);
  const { isLatestVersion, latestVersion, emptyCacheStorage } = useClearCache();

  const clearCache = (): void => {
    emptyCacheStorage(latestVersion);
    setLatestVersionToastOpen(false);
  };

  if (!isLatestVersion && !latestVersionToastOpen) {
    setLatestVersionToastOpen(true);
    toast.info('A new version of this application is available! Click here to update.', {
      autoClose: false,
      closeButton: false,
      onClose: clearCache,
      onClick: clearCache,
    });
  }
  React.useEffect(() => {
    window.Intercom('shutdown');
    window.Intercom('boot', {
      app_id: config.intercom.APP_ID || 'e567lh01',
      ...(authStatus === AuthStatus.SignedIn && sub
        ? {
            user_hash: userHash,
            email,
            name,
            user_name: name,
            conversation_company: group?.groupName,
            admin: permissions?.includes(Permissions.Admin) || false,
            manager:
              permissions?.includes(Permissions.Manager) ||
              permissions?.includes(Permissions.SalesView) ||
              permissions?.includes(Permissions.DealManager) ||
              false,
            sales: permissions?.includes(Permissions.Sales) || permissions?.includes(Permissions.SalesTools) || false,
            configuratorEditor: permissions?.includes(Permissions.Admin) || false,
          }
        : {}),
    });
  }, [authStatus, sub, userHash, email, name, group, permissions]);

  const appTheme = createTheme(theme(selectedTextColor, logoBarColor));

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={appTheme}>
        {useRoutes(routes)}
        <ToastContainer position={toast.POSITION.BOTTOM_RIGHT} closeButton={false} hideProgressBar />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const mapStateToProps = ({
  viewer: { theme: vendorTheme = {} },
  currentUser: { authStatus, group, user: { sub, userHash, email, name, permissions } = unknownUser },
}: AppState): StateProps => {
  const { selectedTextColor = '', logoBarColor = '' } = vendorTheme as Theme;
  return {
    selectedTextColor: (validateColorInput(selectedTextColor) && selectedTextColor) || undefined,
    logoBarColor: (validateColorInput(logoBarColor) && logoBarColor) || undefined,
    authStatus,
    sub,
    userHash,
    email,
    name,
    group,
    permissions,
  };
};

export const App = connect(mapStateToProps)(AppComponent);
